import React from 'react';
import './VidyaAvatar.css';

interface VidyaAvatarProps {
  state?: 'idle' | 'thinking' | 'speaking';
  size?: number;
  className?: string;
}

/**
 * Educational avatar for Vidya.ai - Animated book with lightbulb
 * Represents knowledge and learning insights
 */
export const VidyaAvatar: React.FC<VidyaAvatarProps> = ({
  state = 'idle',
  size = 40,
  className = ''
}) => {
  return (
    <div className={`vidya-avatar ${className}`} style={{ width: size, height: size }}>
      <svg
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`avatar-svg avatar-${state}`}
      >
        {/* Gradient definitions - Updated to cyan-blue */}
        <defs>
          <linearGradient id="bookGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#06B6D4" />
            <stop offset="100%" stopColor="#3B82F6" />
          </linearGradient>
          <linearGradient id="lightGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FCD34D" />
            <stop offset="100%" stopColor="#F59E0B" />
          </linearGradient>
          <radialGradient id="glowGradient">
            <stop offset="0%" stopColor="#FEF3C7" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Glow effect for lightbulb (visible when thinking/speaking) */}
        <circle
          cx="50"
          cy="25"
          r="20"
          fill="url(#glowGradient)"
          className="bulb-glow"
          opacity={state === 'thinking' || state === 'speaking' ? 1 : 0}
        />

        {/* Open book base */}
        <g className="book-group">
          {/* Left page */}
          <path
            d="M 25 40 Q 25 35 30 35 L 45 35 L 45 65 Q 45 70 40 70 L 30 70 Q 25 70 25 65 Z"
            fill="url(#bookGradient)"
            className="book-left-page"
          />
          {/* Right page */}
          <path
            d="M 55 35 L 70 35 Q 75 35 75 40 L 75 65 Q 75 70 70 70 L 60 70 Q 55 70 55 65 Z"
            fill="url(#bookGradient)"
            className="book-right-page"
          />
          {/* Book spine - darker cyan */}
          <rect
            x="45"
            y="35"
            width="10"
            height="35"
            fill="#0891B2"
            className="book-spine"
          />

          {/* Text lines on pages */}
          <g className="page-content" opacity="0.3">
            <rect x="30" y="42" width="12" height="2" fill="#1E293B" />
            <rect x="30" y="47" width="12" height="2" fill="#1E293B" />
            <rect x="30" y="52" width="10" height="2" fill="#1E293B" />

            <rect x="58" y="42" width="12" height="2" fill="#1E293B" />
            <rect x="58" y="47" width="12" height="2" fill="#1E293B" />
            <rect x="58" y="52" width="10" height="2" fill="#1E293B" />
          </g>
        </g>

        {/* Lightbulb above book */}
        <g className="lightbulb-group">
          {/* Bulb */}
          <ellipse
            cx="50"
            cy="25"
            rx="12"
            ry="14"
            fill="url(#lightGradient)"
            className="bulb"
          />
          {/* Bulb base/screw */}
          <rect
            x="46"
            y="35"
            width="8"
            height="6"
            fill="#92400E"
            className="bulb-base"
          />
          {/* Filament */}
          <path
            d="M 47 25 Q 50 20 53 25"
            stroke="#92400E"
            strokeWidth="1"
            fill="none"
            opacity="0.5"
          />
        </g>

        {/* Sparkle/insight indicators */}
        {(state === 'thinking' || state === 'speaking') && (
          <g className="sparkles">
            <circle cx="30" cy="15" r="1.5" fill="#FCD34D" className="sparkle-1" />
            <circle cx="70" cy="20" r="1.5" fill="#FCD34D" className="sparkle-2" />
            <circle cx="35" cy="30" r="1.5" fill="#FCD34D" className="sparkle-3" />
            <circle cx="65" cy="32" r="1.5" fill="#FCD34D" className="sparkle-4" />
          </g>
        )}
      </svg>
    </div>
  );
};

export default VidyaAvatar;
